import { Suspense, useContext, useEffect, useState } from "react"
import { FormContextProvider } from "context/FormContext"
import { ApplicationDataParser } from "components/ApplicationDataParser"
import { generateValidationSchema } from "utilities/schema"
import { UserContext } from "context/UserContext"
import { AuthContext } from "context/AuthContext"
import { sendTealiumEvent } from "tealium"
import { lessThanOneMinuteAgo } from "utilities/helpers"
import { ClientContext } from "context/ClientContext"
import { useLogFormJSON, useLogInitialValues } from "hooks"
import { ApplicationData } from "types"
import { LayoutProvider } from "./LayoutProvider"
import { OptionalLabelManager } from "context/OptionalLabelProvider"
import { DevToolsSidebar } from "devTools/DevToolsSidebar"
import { env } from "env"

export const Authenticated = () => {
  const { token } = useContext(AuthContext)
  const { user } = useContext(UserContext)
  const { client } = useContext(ClientContext)
  const [validationSchema, setValidationSchema] = useState<any>()
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false)
  const [applicationData, setApplicationData] =
    useState<ApplicationData | null>(null)

  useEffect(() => {
    if (!env.DEV) {
      setApplicationData(client?.data ?? ({} as ApplicationData))
    } else {
      import("data/colleges").then(({ COLLEGES }) => {
        const activeCollege = COLLEGES.find(c => c.currentlyActive)!
        setApplicationData(activeCollege.data)
      })
    }
  }, [client])

  const { applicationData: initialFormValues } = user ?? {}

  useLogFormJSON(applicationData ?? ({} as ApplicationData))
  useLogInitialValues(initialFormValues ?? {})

  useEffect(() => {
    if (!applicationData?.meta) return
    const favicon = document.querySelector<HTMLLinkElement>("link#favicon")
    if (applicationData.meta.favicon && favicon) {
      favicon.href = applicationData.meta.favicon
      document.title = `${applicationData.meta.title}`
    }
  }, [applicationData])

  useEffect(() => {
    if (client && applicationData && !validationSchema) {
      const validations = generateValidationSchema(applicationData)
      setValidationSchema(validations)
    }
    if (client && validationSchema) {
      setIsDataLoaded(true)
      if (user && lessThanOneMinuteAgo(user.createdAt)) {
        sendTealiumEvent("app_start", {
          type: "link",
          email_address: user.email
        })
      }
    }
  }, [client, applicationData, validationSchema, user])

  if (!user || !token || !applicationData) return null

  return (
    <FormContextProvider
      validationSchema={validationSchema}
      user={user}
      token={token}
    >
      {env.DEV && DevToolsSidebar && (
        <Suspense fallback={null}>
          <DevToolsSidebar />
        </Suspense>
      )}
      <LayoutProvider
        applicationData={applicationData}
        isDataLoaded={isDataLoaded}
      >
        <OptionalLabelManager>
          <ApplicationDataParser {...applicationData} />
        </OptionalLabelManager>
      </LayoutProvider>
    </FormContextProvider>
  )
}
